/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { ConsumptionArc } from './consumptionModel';
import { KgDirtree, KgDoc } from './types';

// Company-altitude fleet graph (PLAT-1646): one NON-derived graph of the
// whole company — every node a repo, every edge a cross-repo relationship —
// built client-side from repo-level facts (the dirtree's org bands plus the
// catalog's declared API relations). Nothing here touches the giant merged
// doc or its shards: the per-repo graphs stay where they already render
// well, one deep link away behind each box.

export type CrossRepoLink = {
  from: string; // repo slug
  to: string;
  family: string;
  count: number;
  crossOrg: boolean;
};

// Relationships FOUND by consolidating the graphs: dirtree edges whose
// endpoints resolve to different repos. Sparse until cross-repo linking
// lands (PLAT-1650 upstream + agentic edge inference) — and this layer
// grows automatically as it does. Cluster-group endpoints are excluded:
// a community aggregate is not a repo relationship. Note: derive
// aggregates each edge once at its divergence level, so a CROSS-ORG
// symbol edge surfaces as an org-band pair (single segment, skipped
// here) — repo-attributable cross-org links come from the manifest
// layer today; crossOrg stays for when deeper aggregation ships.
export const crossRepoLinks = (edges: KgDirtree['edges']): CrossRepoLink[] => {
  const repoOf = (path: string): string | null => {
    const seg = path.split('/');
    if (seg[0] === '(root)') return null;
    return seg.length >= 2 ? `${seg[0]}/${seg[1]}` : null;
  };
  const byPair = new Map<string, CrossRepoLink>();
  for (const e of edges) {
    const from = repoOf(e.source);
    const to = repoOf(e.target);
    if (!from || !to || from === to) continue;
    const k = `${from} ${to} ${e.family}`;
    const agg = byPair.get(k) ?? {
      from,
      to,
      family: e.family,
      count: 0,
      crossOrg: from.split('/')[0] !== to.split('/')[0],
    };
    agg.count += e.count;
    byPair.set(k, agg);
  }
  return [...byPair.values()].sort((a, b) => b.count - a.count);
};

// relations.json (PLAT-1646 fleet_relations pass): cross-repo edges lifted
// from build manifests — gradle/npm/cargo coordinates and Dockerfile FROM
// lines — resolved by naming rules plus agentic adjudication. Published
// best-effort by the fleet workflow; absent until its next run.
export type FleetRelations = {
  repos: number;
  edges: {
    from: string;
    to: string;
    kind: string; // depends_on | versions_managed_by
    via: string;
    provenance: string;
    confidence: number;
  }[];
  connectors: { artifact: string; consumers: string[] }[];
  unresolved: Record<string, string[]>;
  unconnected: string[];
  skipped: string[];
  // Set when this build's pass failed and the workflow carried the previous
  // build's relations forward — stale data must say so.
  carried_from?: string;
};

// Store data is external data: validate the artifact's shape at the
// boundary instead of casting. Returns null for a non-object (the caller
// treats that as a store error, not an absence); malformed entries are
// dropped item-by-item so one drifted edge can't take down the render.
export const parseFleetRelations = (raw: unknown): FleetRelations | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const strArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  return {
    repos: typeof r.repos === 'number' ? r.repos : 0,
    edges: (Array.isArray(r.edges) ? r.edges : []).filter(
      (e): e is FleetRelations['edges'][number] =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as Record<string, unknown>).from === 'string' &&
        typeof (e as Record<string, unknown>).to === 'string' &&
        typeof (e as Record<string, unknown>).kind === 'string',
    ),
    connectors: (Array.isArray(r.connectors) ? r.connectors : []).filter(
      (c): c is FleetRelations['connectors'][number] =>
        typeof c === 'object' &&
        c !== null &&
        typeof (c as Record<string, unknown>).artifact === 'string',
    ),
    unresolved:
      typeof r.unresolved === 'object' && r.unresolved !== null && !Array.isArray(r.unresolved)
        ? (r.unresolved as Record<string, string[]>)
        : {},
    unconnected: strArray(r.unconnected),
    skipped: strArray(r.skipped),
    carried_from:
      typeof r.carried_from === 'string' ? r.carried_from : undefined,
  };
};

// The synthetic company doc the incumbent renderer draws: repo boxes with
// their store facts as captions, consumption + found links as families.
// Repo nodes reuse the _dir affordance so the renderer offers "open" on
// every box — the deep link into that repo's own graph.
//
// A relationship endpoint WITHOUT a published graph still gets a box (a
// "ghost": no store facts, caption names the gap). The local audit
// (2026-08-25) found 7 of 11 real catalog consumption pairs silently
// hidden because one endpoint wasn't enrolled in graph-refresh — the map
// must show the missing territory, not crop it.
export const buildCompanyDoc = (
  tree: KgDirtree,
  consumption: ConsumptionArc[],
  found: CrossRepoLink[],
  relations?: FleetRelations | null,
): KgDoc & { ghosts: string[] } => {
  const orgs = new Set(
    tree.dirs.filter(d => d.parent === '' && d.path !== '(root)').map(d => d.path),
  );
  const repos = tree.dirs
    .filter(d => orgs.has(d.parent))
    .sort((a, b) => b.symbols - a.symbols);
  const known = new Set(repos.map(r => r.path));
  // Two-sided case-insensitive matching (the github-org
  // preserves-login-case lesson, PR #231→#232): catalog slugs arrive
  // lowercased while the store carries GitHub's original spelling — every
  // edge endpoint canonicalizes onto the tree's spelling so it lands on
  // the real box instead of minting a spurious ghost.
  const canonical = new Map(repos.map(r => [r.path.toLowerCase(), r.path]));
  const canon = (slug: string) => canonical.get(slug.toLowerCase()) ?? slug;
  const ghosts = new Set<string>();
  const links: NonNullable<KgDoc['links']> = [];
  consumption.forEach((arc, i) => {
    const from = canon(arc.from);
    const to = canon(arc.to);
    for (const end of [from, to]) {
      if (!known.has(end)) ghosts.add(end);
    }
    links.push({
      id: `api${i}`,
      source: from,
      target: to,
      relation: 'consumes API (catalog)',
      count: arc.apis.length,
    });
  });
  found.forEach((link, i) => {
    // Found links come from the merged graph, so both ends have graphs by
    // construction — no ghost can appear here.
    if (!known.has(link.from) || !known.has(link.to)) return;
    links.push({
      id: `sym${i}`,
      source: link.from,
      target: link.to,
      relation: `${link.family} (merged graph)`,
      count: link.count,
    });
  });
  // Manifest edges aggregate per pair+kind (several coordinates between the
  // same two repos are one relationship, sized by how many). Endpoints
  // outside the store become ghosts like the catalog ones — defensive
  // future-proofing: today fleet_relations resolves only into the gathered
  // fleet, so unenrolled targets land in its `unresolved` list instead.
  // Agentic adjudications keep their provenance in the family name — an
  // inferred edge must never read as a deterministic one.
  const manifestPairs = new Map<string, { edge: NonNullable<KgDoc['links']>[number] }>();
  for (const e of relations?.edges ?? []) {
    const from = canon(e.from);
    const to = canon(e.to);
    for (const end of [from, to]) {
      if (!known.has(end)) ghosts.add(end);
    }
    const agentic = e.provenance?.includes('agentic');
    const relation =
      e.kind === 'versions_managed_by'
        ? 'versions managed by (build manifests)'
        : `depends on (build manifests${agentic ? ', agentic' : ''})`;
    const k = `${from}\u0000${to}\u0000${relation}`;
    const existing = manifestPairs.get(k);
    if (existing) {
      existing.edge.count = (existing.edge.count ?? 1) + 1;
    } else {
      manifestPairs.set(k, {
        edge: {
          id: `man${manifestPairs.size}`,
          source: from,
          target: to,
          relation,
          count: 1,
        },
      });
    }
  }
  links.push(...[...manifestPairs.values()].map(p => p.edge));
  return {
    built_at_commit: tree.built_at_commit,
    nodes: [
      ...repos.map(r => ({
        id: r.path,
        label: r.path,
        _dir: true,
        source_file: `${r.symbols.toLocaleString()} symbols · ${r.files.toLocaleString()} files`,
      })),
      ...[...ghosts].sort().map(slug => ({
        id: slug,
        label: slug,
        _dir: true,
        source_file: 'no graph published yet — not enrolled in graph-refresh',
      })),
    ],
    links,
    ghosts: [...ghosts].sort(),
  };
};
